import { useContext, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Flex, Form, Modal, Typography } from "antd";
import { ClearOutlined } from "@ant-design/icons";

import { UserContext } from "@/context/UserContext";
import {
  TASK_EXPORT_ENABLED,
  TASK_EXPORT_FIELDS,
  TASK_EXPORT_FIELDS_LOCAL_STORAGE_KEY,
  getTaskExportFieldNames,
} from "@/constants/tasks";
import { exportTasks } from "@/api/tasks";
import { getPluralizedWord } from "@/utils";

const { Text } = Typography;

const getStorageKey = (userUuid) =>
  `${userUuid || "anonymous"}_${TASK_EXPORT_FIELDS_LOCAL_STORAGE_KEY}`;

const readStoredFields = (userUuid) => {
  try {
    const raw = localStorage.getItem(getStorageKey(userUuid));
    const stored = raw ? JSON.parse(raw) : null;
    const available = getTaskExportFieldNames();

    if (!Array.isArray(stored)) return available;

    const filtered = stored.filter((field) => available.includes(field));
    return filtered.length ? filtered : available;
  } catch {
    return getTaskExportFieldNames();
  }
};

const ExportTasksModal = ({
  tasks = [],
  visible,
  onSave,
  onCancel,
  onClearAndCancel,
  isAdmins = false,
}) => {
  const [form] = Form.useForm();
  const { user } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const fieldsOptions = useMemo(
    () =>
      TASK_EXPORT_FIELDS.map((field) => ({
        label: field.title,
        value: field.name,
      })),
    [],
  );

  useEffect(() => {
    if (!visible) return;

    form.setFieldsValue({
      fields: readStoredFields(user?.user_uuid),
    });
  }, [form, user?.user_uuid, visible]);

  const handleCancel = () => {
    setError(null);
    onCancel();
  };

  const handleSave = async (values) => {
    if (!TASK_EXPORT_ENABLED) return;

    setError(null);
    setIsLoading(true);

    try {
      localStorage.setItem(
        getStorageKey(user?.user_uuid),
        JSON.stringify(values.fields),
      );

      const file = await exportTasks(
        {
          task_uuids: tasks,
          fields: values.fields,
        },
        isAdmins,
      );

      onSave(file);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onOk = () => {
    form
      .validateFields()
      .then(handleSave)
      .catch((info) => {
        console.log("Validation error:", info);
      });
  };

  return (
    <Modal
      maskClosable={false}
      title="Экспорт задач"
      open={visible}
      onCancel={handleCancel}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button
            key="clear"
            color="danger"
            variant="link"
            icon={<ClearOutlined />}
            onClick={onClearAndCancel}
            style={{ marginRight: "auto", paddingInline: 0 }}
          >
            Отмена и очистка выбора
          </Button>

          <Button key="back" onClick={handleCancel}>
            Отмена
          </Button>

          <Button
            key="submit"
            type="primary"
            loading={isLoading}
            onClick={onOk}
          >
            Выгрузить
          </Button>
        </div>
      }
    >
      <Flex vertical gap={10}>
        {!!tasks.length && (
          <Text>
            Выбрано {tasks.length}{" "}
            {getPluralizedWord(tasks.length, ["задача", "задачи", "задач"])}
          </Text>
        )}

        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item
            name="fields"
            label="Укажите поля для выгрузки"
            rules={[
              {
                required: true,
                type: "array",
                min: 1,
                message: "Выберите хотя бы одно поле",
              },
            ]}
          >
            <Checkbox.Group
              options={fieldsOptions}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 5,
              }}
            />
          </Form.Item>

          {error && <Text type="danger">{error}</Text>}
        </Form>
      </Flex>
    </Modal>
  );
};

export default ExportTasksModal;
